/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { EntityStore } from 'src/core/types/EntityStore';
import { GptJobSwagger, GptPredictionDecision, GptPredictionDocument } from 'src/customText/core/services/gpt/customTextGptSwagger';
import {
    CustomTextExportedProjectDocumentSwagger,
    CustomTextExportedProjectEntityLabelSwagger
} from 'src/customText/core/services/projects/customTextProjectSwaggers';
import { CustomTextClassStore } from 'src/customText/core/stores/CustomTextClassStore';
import { CustomTextDocumentMetadata } from 'src/customText/core/stores/CustomTextDocumentStore';
import { CustomTextEntityLabelStore, CustomTextEntityLabelStoreData } from 'src/customText/core/stores/CustomTextEntityLabelStore';
import { CustomTextGptPredictedDocument, GptEntityLabel } from 'src/customText/core/stores/CustomTextGptPredictedDocument';
import { CustomTextProjectKind } from 'src/customText/core/stores/CustomTextProjectStore';
import { GptJobStore } from 'src/modules/gptPrediction/stores/GptJobStore';
import { getLabelId } from 'src/modules/gptPrediction/utils/gptUtils';

export const gptJobSwaggerToGptJobStore = (swagger: GptJobSwagger): GptJobStore => {
    const elapsedSeconds = Math.ceil(swagger.elapsedTime % 60);
    const elapsedMinutes = Math.floor((swagger.elapsedTime / 60) % 60);
    const elapsedHours = Math.floor((swagger.elapsedTime / (60 * 60)) % 24);

    return new GptJobStore(swagger.jobId, {
        errors: swagger.errors,
        elapsedTime: `${elapsedHours}:${elapsedMinutes}:${elapsedSeconds}`,
        status: swagger.status === 'running' ? 'inProgress' : swagger.status,
        createdDateTime: swagger.createdDateTime ? new Date(swagger.createdDateTime) : null,
        expirationDateTime: swagger.expirationDateTime ? new Date(swagger.expirationDateTime) : null,
        lastUpdatedDateTime: swagger.status === 'succeeded' ? new Date(swagger.lastUpdatedDateTime) : null
    });
};

export const validateGptPredictionWithUserLabel = (
    predictedEntity: CustomTextExportedProjectEntityLabelSwagger,
    originalLabel: CustomTextEntityLabelStore
) => {
    const predictedEntityStartIndex = predictedEntity.offset;
    const predictedEntityEndIndex = predictedEntity.offset + predictedEntity.length - 1;

    /**
     * case 1: sample text example
     *                ----           original label
     *         ---------             predicted label
     * case 2: sample text example
     *                ----           original label
     *                ----           predicted label
     */
    return (
        (predictedEntityStartIndex <= originalLabel.startCharIndex &&
            predictedEntityEndIndex >= originalLabel.startCharIndex &&
            predictedEntityEndIndex <= originalLabel.endCharIndex) ||
        /**
         * case 3: sample text example
         *                ----           original label
         *                  ----------   predicted label
         */
        (predictedEntityStartIndex >= originalLabel.startCharIndex &&
            predictedEntityStartIndex <= originalLabel.endCharIndex &&
            predictedEntityEndIndex >= originalLabel.endCharIndex) ||
        /**
         * case 4: sample text example
         *                ----           original label
         *         -------------------   predicted label
         */
        (predictedEntityStartIndex <= originalLabel.startCharIndex && predictedEntityEndIndex >= originalLabel.endCharIndex) ||
        /**
         * case 5: sample text example
         *            ------------       original label
         *                ----           predicted label
         */
        (predictedEntityStartIndex >= originalLabel.startCharIndex && predictedEntityEndIndex <= originalLabel.endCharIndex)
    );
};

const validatePrediction = (
    predictedEntity: CustomTextExportedProjectEntityLabelSwagger,
    predictedEntities: CustomTextExportedProjectEntityLabelSwagger[],
    originalDocumentData: CustomTextDocumentMetadata
): boolean => {
    const isOverlappingWithOriginalLabel = originalDocumentData.entityLabels.reduce(
        (acc, originalLabel) => acc || validateGptPredictionWithUserLabel(predictedEntity, originalLabel),
        false
    );

    if (isOverlappingWithOriginalLabel) {
        return false;
    }

    const predictedEntityStartIndex = predictedEntity.offset;
    const predictedEntityEndIndex = predictedEntity.offset + predictedEntity.length - 1;
    // Filter out the given predicted entity.
    const filteredPredictedEntities = predictedEntities.filter(
        e => !(e.category === predictedEntity.category && e.length === predictedEntity.length && e.offset === predictedEntity.offset)
    );
    const isOverlappingWithPredictedEntity = filteredPredictedEntities.reduce((acc, otherPredictedLabel) => {
        const otherPredictedLabelStartIndex = otherPredictedLabel.offset;
        const otherPredictedLabelEndIndex = otherPredictedLabel.offset + otherPredictedLabel.length - 1;

        return (
            acc ||
            // Return only the longest labels
            (predictedEntityStartIndex >= otherPredictedLabelStartIndex && predictedEntityEndIndex <= otherPredictedLabelEndIndex)
        );
    }, false);

    if (isOverlappingWithPredictedEntity) {
        return false;
    }

    return true;
};

export const gptDocumentSwaggerToStore = (
    document: CustomTextExportedProjectDocumentSwagger,
    entities: EntityStore[],
    classes: CustomTextClassStore[],
    originalDocumentStores: CustomTextDocumentMetadata[]
): CustomTextGptPredictedDocument => {
    const predictedClass = classes.find(c => c.name === document.class?.category);
    const predictedEntities =
        document.entities
            ?.reduce<CustomTextExportedProjectEntityLabelSwagger[]>((acc, e) => [...acc, ...e.labels], [])
            .filter(e => entities.some(entity => entity.name === e.category)) ?? [];

    return new CustomTextGptPredictedDocument({
        name: document.location,
        entityLabels:
            predictedEntities
                .reduce((acc, predictedEntity) => {
                    const isValid = validatePrediction(
                        predictedEntity,
                        acc,
                        originalDocumentStores.find(d => d.name === document.location)
                    );

                    if (isValid) {
                        acc = [...acc, predictedEntity];
                    }

                    return acc;
                }, [])
                .map<GptEntityLabel>(l => {
                    const labelData: CustomTextEntityLabelStoreData = {
                        startCharIndex: l.offset,
                        labelType: 'gptPredicted',
                        endCharIndex: l.offset + l.length - 1,
                        entityId: entities.find(es => es.name === l.category).id
                    };

                    return { labelId: getLabelId(labelData), ...new CustomTextEntityLabelStore(labelData) };
                }) ?? [],
        classLabels:
            document.classes?.map(c => new CustomTextClassStore({ name: c.category, id: classes.find(pc => pc.name === c.category).id })) ??
            (predictedClass && [
                new CustomTextClassStore({ name: document.class?.category, id: predictedClass?.id ?? document.class?.category })
            ]) ??
            []
    });
};

export const gptDocumentStoreToGptPredictedDocumentSwagger = (
    store: CustomTextGptPredictedDocument,
    documentMetadata: CustomTextDocumentMetadata,
    entities: EntityStore[],
    projectKind: CustomTextProjectKind
): GptPredictionDocument => {
    let classLabelToSubmit: CustomTextClassStore;
    const { acceptedClassLabels, rejectedClassLabels } = store;

    if (acceptedClassLabels.length > 0) {
        [classLabelToSubmit] = acceptedClassLabels;
    } else if (rejectedClassLabels.length > 0) {
        [classLabelToSubmit] = rejectedClassLabels;
    }

    return {
        location: store.name,
        language: documentMetadata.language,
        ...(projectKind === 'CustomSingleLabelClassification' && {
            class: classLabelToSubmit && {
                category: classLabelToSubmit?.name,
                predictionDecision: store.rejectedClassLabels.length > 0 ? 'Rejected' : 'Accepted'
            }
        }),
        ...(projectKind === 'CustomMultiLabelClassification' && {
            classes:
                (store.acceptedClassLabels.length || store.rejectedClassLabels.length) &&
                store.acceptedClassLabels
                    .map(l => ({ category: l.name, predictionDecision: 'Accepted' as GptPredictionDecision }))
                    .concat(
                        store.rejectedClassLabels.map(l => ({ category: l.name, predictionDecision: 'Rejected' as GptPredictionDecision }))
                    )
        }),
        dataset: documentMetadata.documentDataset,
        ...(projectKind === 'CustomEntityRecognition' && {
            entities: [
                {
                    regionLength: null,
                    regionOffset: null,
                    labels: store.acceptedEntityLabels
                        .map(entityLabel => ({
                            offset: entityLabel.startCharIndex,
                            predictionDecision: 'Accepted' as GptPredictionDecision,
                            category: entities.find(e => e.id === entityLabel.entityId).name,
                            length: entityLabel.endCharIndex - entityLabel.startCharIndex + 1
                        }))
                        .concat(
                            ...store.rejectedEntityLabels.map(entityLabel => ({
                                offset: entityLabel.startCharIndex,
                                predictionDecision: 'Rejected' as GptPredictionDecision,
                                category: entities.find(e => e.id === entityLabel.entityId).name,
                                length: entityLabel.endCharIndex - entityLabel.startCharIndex + 1
                            }))
                        )
                }
            ]
        })
    };
};
